import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  useTheme,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from "@clerk/clerk-react";
import { tokens } from '../../theme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';

const KeeperSettings = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();
  
  const [settings, setSettings] = useState({
    keeper_max_years_kept: 2,    // 2 years an owner can keep
    keeper_highest_round: 3,     // no rounds of 3 (cannot keep rounds 1-3)
    keeper_round_cost: 2,        // 2 rounds of cost
    keeper_undrafted_round: 10   // round 10 for undrafted
  });
  
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Helper function to get API headers with authentication
  const getApiHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };
    
    if (user) {
      headers["X-Clerk-User-Id"] = user.id || "unknown";
      headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
      headers["X-Clerk-Username"] = user.username || "";
    }
    
    return headers;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch league data from your API (includes keeper settings)
        const headers = getApiHeaders();
        const fetchUrl = `/api/leagues/${leagueId}`;
        
        console.log('[KeeperSettings] Fetching from URL:', fetchUrl);
        console.log('[KeeperSettings] League ID from params:', leagueId);
        
        const response = await fetch(fetchUrl, { headers });
        if (response.ok) {
          const leagueData = await response.json();
          
          // Set league details for display
          setLeagueDetails({
            name: leagueData.name,
            season: leagueData.season || new Date().getFullYear(),
            total_rosters: leagueData.total_rosters
          });
          
          // Extract keeper settings from league data, use defaults if not set
          setSettings({
            keeper_max_years_kept: leagueData.keeper_max_years_kept ?? 2,     // 2 years an owner can keep
            keeper_highest_round: leagueData.keeper_highest_round ?? 3,       // no rounds of 3 (cannot keep rounds 1-3)
            keeper_round_cost: leagueData.keeper_round_cost ?? 2,             // 2 rounds of cost
            keeper_undrafted_round: leagueData.keeper_undrafted_round ?? 10   // round 10 for undrafted
          });

          // Also fetch Sleeper details for additional league info if needed
          try {
            const sleeperResponse = await fetch(`https://api.sleeper.app/v1/league/${leagueData?.external_league_id || leagueId}`);
            if (sleeperResponse.ok) {
              const sleeperData = await sleeperResponse.json();
              // Merge with existing league details
              setLeagueDetails(prev => ({
                ...prev,
                ...sleeperData
              }));
            }
          } catch (sleeperError) {
            console.warn('Failed to fetch Sleeper details:', sleeperError);
            // Continue without Sleeper data
          }
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load keeper settings');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && leagueId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, leagueId]); // getApiHeaders is stable and only uses user

  const handleInputChange = (field) => (event) => {
    const value = parseInt(event.target.value) || 0;
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const headers = getApiHeaders();
      const url = `/api/leagues/${leagueId}/`;
      
      console.log('[KeeperSettings] Saving to URL:', url);
      console.log('[KeeperSettings] League ID:', leagueId);
      console.log('[KeeperSettings] Settings payload:', settings);
      console.log('[KeeperSettings] Headers:', headers);
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(settings),
      });

      console.log('[KeeperSettings] Response status:', response.status);
      console.log('[KeeperSettings] Response URL:', response.url);

      if (!response.ok) {
        const responseText = await response.text();
        console.log('[KeeperSettings] Response body:', responseText);
        throw new Error(`Failed to save settings: ${response.status} ${response.statusText} - ${responseText}`);
      }

      setSuccess('Keeper settings saved successfully!');
      
    } catch (err) {
      console.error('Error saving keeper settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    navigate(`/leagues/${leagueId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.blueAccent[500] }} />
        <Typography variant="h6" sx={{ color: colors.grey[300] }}>
          Loading keeper settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      {/* Navigation */}
      <Box sx={{ mb: 3 }}>
        <IconButton
          onClick={handleBackClick}
          sx={{ 
            color: colors.grey[300], 
            mb: 2,
            '&:hover': {
              backgroundColor: colors.primary[600],
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs sx={{ color: colors.grey[400] }}>
          <Link
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ 
              cursor: 'pointer',
              '&:hover': { color: colors.blueAccent[400] }
            }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            onClick={handleBackClick}
            sx={{ 
              cursor: 'pointer',
              '&:hover': { color: colors.blueAccent[400] }
            }}
          >
            {leagueDetails?.name || 'League'}
          </Link>
          <Typography color={colors.grey[100]}>
            Keeper Settings
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon
            sx={{
              fontSize: 40,
              color: colors.blueAccent[500],
              mr: 2,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                mb: 1,
              }}
            >
              Keeper Settings
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.grey[300],
                fontWeight: 400,
              }}
            >
              {leagueDetails?.name || 'Configure keeper rules for this league'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Settings Form */}
      <Grid container spacing={3}>
        {/* Settings Cards */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: colors.grey[100],
                fontWeight: 600,
                mb: 3,
              }}
            >
              Keeper Rules Configuration
            </Typography>

            <Grid container spacing={3}>
              {/* Max Years Kept */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.grey[700]}`,
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: colors.grey[100], mb: 1, fontWeight: 600 }}
                    >
                      Maximum Years Kept
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[300], mb: 2 }}
                    >
                      How many years can a single team keep a player?
                    </Typography>
                    <TextField
                      type="number"
                      value={settings.keeper_max_years_kept}
                      onChange={handleInputChange('keeper_max_years_kept')}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 1, max: 10 }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.primary[600],
                          '& fieldset': {
                            borderColor: colors.grey[700],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: colors.grey[100],
                          textAlign: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Highest Keeper Round */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.grey[700]}`,
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                                        <Typography
                      variant="h6"
                      sx={{ color: colors.grey[100], mb: 1, fontWeight: 600 }}
                    >
                      Cannot Keep Rounds
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[300], mb: 2 }}
                    >
                      Players drafted in rounds 1 through {settings.keeper_highest_round} cannot be kept
                    </Typography>
                      <TextField
                        type="number"
                        value={settings.keeper_highest_round}
                        onChange={handleInputChange('keeper_highest_round')}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 1, max: 16 }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.primary[600],
                          '& fieldset': {
                            borderColor: colors.grey[700],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: colors.grey[100],
                          textAlign: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Round Penalty */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.grey[700]}`,
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: colors.grey[100], mb: 1, fontWeight: 600 }}
                    >
                      Round Cost
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[300], mb: 2 }}
                    >
                      Which round do you give up to keep a player? (e.g., round 10 player costs round 8 pick)
                    </Typography>
                                          <TextField
                        type="number"
                        value={settings.keeper_round_cost}
                        onChange={handleInputChange('keeper_round_cost')}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 5 }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.primary[600],
                          '& fieldset': {
                            borderColor: colors.grey[700],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: colors.grey[100],
                          textAlign: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Undrafted Player Round */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.grey[700]}`,
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: colors.grey[100], mb: 1, fontWeight: 600 }}
                    >
                      Undrafted Player Round
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[300], mb: 2 }}
                    >
                      Round cost for players who went undrafted (does not apply to players who were dropped to free agency)
                    </Typography>
                                          <TextField
                        type="number"
                        value={settings.keeper_undrafted_round}
                        onChange={handleInputChange('keeper_undrafted_round')}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 1, max: 20 }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.primary[600],
                          '& fieldset': {
                            borderColor: colors.grey[700],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: colors.grey[100],
                          textAlign: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Alerts */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 3,
                  backgroundColor: colors.redAccent[800],
                  color: colors.redAccent[100],
                  '& .MuiAlert-icon': {
                    color: colors.redAccent[300],
                  },
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mt: 3,
                  backgroundColor: colors.greenAccent[800],
                  color: colors.greenAccent[100],
                  '& .MuiAlert-icon': {
                    color: colors.greenAccent[300],
                  },
                }}
              >
                {success}
              </Alert>
            )}

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                onClick={handleSave}
                disabled={saving}
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: colors.greenAccent[500],
                  },
                  '&:disabled': {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[500],
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Info Panel */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoIcon sx={{ color: colors.blueAccent[500], mr: 1 }} />
              <Typography
                variant="h6"
                sx={{ color: colors.grey[100], fontWeight: 600 }}
              >
                How Keeper Rules Work
              </Typography>
            </Box>
            
            <Divider sx={{ borderColor: colors.grey[700], mb: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: colors.blueAccent[300], fontWeight: 600, mb: 1 }}
              >
                Example with Current Settings:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], lineHeight: 1.6 }}
              >
                • A player drafted in round 7 last year can be kept for a round {7 - settings.keeper_round_cost} pick
                <br />
                • Players drafted in rounds 1 through {settings.keeper_highest_round} cannot be kept
                <br />
                • Undrafted players cost a round {settings.keeper_undrafted_round} pick to keep
                <br />
                • Players can be kept for a maximum of {settings.keeper_max_years_kept} years
              </Typography>
            </Box>

            <Divider sx={{ borderColor: colors.grey[700], mb: 2 }} />

            <Typography
              variant="body2"
              sx={{ color: colors.grey[400], fontStyle: 'italic' }}
            >
              These settings will be used to calculate keeper values and determine which players are eligible to be kept for next season.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default KeeperSettings;
